import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { ChatCircleDots, X, PaperPlaneTilt, Sparkle, ArrowClockwise } from "@phosphor-icons/react";

const SEED_PROMPTS = [
  "How do I apply for casual leave?",
  "Where do I check my payslip?",
  "What is a KRA vs KPI?",
  "How does the 9-box grid work?",
  "Explain PF vs ESIC in simple terms",
];

export default function AskZenith() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  if (!user) return null;

  const send = async (t) => {
    const payload = (t ?? text).trim();
    if (!payload || busy) return;
    setMsgs((m) => [...m, { role: "user", content: payload }]);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/ask", { message: payload, session_id: sessionId });
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiError(e));
      setMsgs((m) => [...m, { role: "assistant", content: "Sorry, I couldn't reach the assistant. Try again in a moment." }]);
    }
    setBusy(false);
  };

  const reset = () => { setMsgs([]); setSessionId(null); };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-zenith-launcher"
          className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 pl-3 pr-4 py-2.5 bg-gradient-to-r from-zenith-blue via-zenith-magenta to-zenith-teal hover:brightness-110 text-white rounded-full shadow-lg shadow-zenith-magenta/30 transition-all duration-200 hover:scale-[1.03]"
        >
          <div className="relative w-7 h-7 rounded-full bg-adb-green grid place-items-center">
            <Sparkle size={14} weight="fill" className="text-adb-navy" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-white animate-pulse"></span>
          </div>
          <span className="font-display font-bold text-sm">Ask Zenith360</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[420px] h-[70vh] max-h-[640px] bg-white border border-zinc-200 rounded-sm shadow-2xl flex flex-col" data-testid="ask-zenith-panel">
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between bg-gradient-to-r from-zenith-blue via-adb-navy to-zenith-blue-700 text-white rounded-t-sm">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-zenith-teal via-zenith-green to-zenith-magenta grid place-items-center">
                <Sparkle size={16} weight="fill" className="text-white drop-shadow" />
              </div>
              <div>
                <div className="font-display font-bold text-sm">Ask Zenith360</div>
                <div className="text-[10px] text-white/60">Claude Sonnet 4.5 · HR-tuned</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {msgs.length > 0 && (
                <button onClick={reset} className="p-1.5 hover:bg-white/10 rounded-sm" title="New chat" data-testid="ask-zenith-new">
                  <ArrowClockwise size={14} />
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-sm" data-testid="ask-zenith-close">
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-zinc-50/50">
            {msgs.length === 0 && (
              <div className="text-center pt-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-zenith-teal via-zenith-green to-zenith-magenta grid place-items-center mx-auto shadow-lg shadow-zenith-magenta/20">
                  <Sparkle size={22} weight="fill" className="text-adb-green" />
                </div>
                <div className="font-display text-base font-bold mt-3">Hi {user.name?.split(" ")[0]} — how can I help?</div>
                <div className="text-xs text-zinc-500 mt-1 mb-4">Try one of these to get started:</div>
                <div className="space-y-1.5 text-left">
                  {SEED_PROMPTS.map((p) => (
                    <button
                      key={p}
                      onClick={() => send(p)}
                      data-testid={`ask-zenith-seed-${p.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                      className="w-full text-left text-xs px-3 py-2 border border-zinc-200 hover:border-adb-navy hover:bg-white rounded-sm transition-colors"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm ${m.role === "user" ? "bg-adb-navy text-white" : "bg-white border border-zinc-200 text-zinc-800"}`}>
                  <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-200 rounded-sm px-3 py-2 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-adb-green animate-bounce"></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-adb-green animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-1.5 h-1.5 rounded-full bg-adb-green animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-zinc-200 p-2.5 bg-white rounded-b-sm">
            <div className="relative">
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask about leaves, payslips, KRAs…"
                rows={2}
                data-testid="ask-zenith-input"
                className="rounded-sm text-sm resize-none pr-12 border-zinc-300 focus-visible:border-adb-navy focus-visible:ring-adb-navy"
              />
              <Button
                size="sm"
                onClick={() => send()}
                disabled={busy || !text.trim()}
                data-testid="ask-zenith-send"
                className="absolute right-1.5 bottom-1.5 h-8 w-8 p-0 bg-adb-navy hover:bg-adb-navy-800 rounded-sm"
              >
                <PaperPlaneTilt size={14} weight="fill" />
              </Button>
            </div>
            <div className="text-[9px] text-zinc-400 mt-1 flex items-center gap-1">
              <Sparkle size={9} /> Powered by Zenith360 · replies may contain errors — verify with HR
            </div>
          </div>
        </div>
      )}
    </>
  );
}
